import { create } from 'zustand';

export type ExportStatus = 'pending' | 'ready' | 'failed';

export interface ActiveExport {
  exportId: string;
  resource: string;
  status: ExportStatus;
  downloadUrl?: string;
  fileName?: string;
  createdAt: number;
}

interface ExportsStore {
  activeExports: Record<string, ActiveExport>;

  /** Add or update an export (used when enqueuing an async export). */
  upsert: (exportEntry: ActiveExport) => void;
  /** Mark an export as ready and attach the download URL. */
  markReady: (exportId: string, downloadUrl: string, fileName?: string) => void;
  /** Mark an export as failed. */
  markFailed: (exportId: string) => void;
  /** Remove an export from the active map. */
  remove: (exportId: string) => void;
}

const useExportsStore = create<ExportsStore>((set) => ({
  activeExports: {},

  upsert: (exportEntry) => {
    set((state) => ({
      activeExports: {
        ...state.activeExports,
        [exportEntry.exportId]: exportEntry,
      },
    }));
  },

  markReady: (exportId, downloadUrl, fileName) => {
    set((state) => {
      const current = state.activeExports[exportId];
      if (!current) return state;
      return {
        activeExports: {
          ...state.activeExports,
          [exportId]: {
            ...current,
            status: 'ready',
            downloadUrl,
            fileName: fileName ?? current.fileName,
          },
        },
      };
    });
  },

  markFailed: (exportId) => {
    set((state) => {
      const current = state.activeExports[exportId];
      if (!current) return state;
      return {
        activeExports: {
          ...state.activeExports,
          [exportId]: { ...current, status: 'failed' },
        },
      };
    });
  },

  remove: (exportId) => {
    set((state) => {
      if (!state.activeExports[exportId]) return state;
      const next = { ...state.activeExports };
      delete next[exportId];
      return { activeExports: next };
    });
  },
}));

export default useExportsStore;
